import { useState, useEffect } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useSelector, useDispatch } from 'react-redux';
import { Alert } from '@material-ui/lab';
import { Chip } from '@material-ui/core';
import clsx from 'clsx';

import { getRequest } from 'utils/api';
import { getGigOwner } from 'utils/helpers';
import { setGig } from 'features/gigsSlice';
import Toast from 'utils/toast.jsx';
import IconMainContainer from 'components/IconMainContainer';
import MilestoneScreenshots from './MilestoneScreenshots';
import GuaranteedGainsProgress from './GuaranteedGainsProgress';
import useCurrentProfile from 'hooks/useCurrentProfile';
import styles from 'styles/components/milestones/Milestone.module.scss';

const ONLYFANS_NETWORK_ID = 6;

const alertStyleNotActive = {
  padding: '20px 15px',
  width: '90%',
  margin: '0 auto',
  marginTop: 10,
};

const Deliverables = ({ gig, milestone, handleUpdateMilestone, agencyView = false }) => {
  const [onlyFansUsername, setOnlyFansUsername] = useState('');

  const currentUser = useSelector(state => state.auth.currentUser);
  const profile = useCurrentProfile();
  const dispatch = useDispatch();
  const gigOwner = getGigOwner(currentUser, gig.user_id);
  const influencerAssigned =
    gig.profile_id === profile?.id || currentUser?.profile_ids?.includes(gig.profile_id);
  const cancelledGig = gig?.status === 'cancelled';

  const getLabel = () => {
    if (milestone.status === 'approved') {
      return 'Approved & Paid';
    } else if (milestone.status === 'awaiting_approval') {
      return 'Delivered';
    } else if (milestone.status === 'rejected') {
      return 'Not accepted';
    }

    return 'Awaiting delivery';
  };

  const updateMilestone = (id, count) => {
    if (handleUpdateMilestone) {
      // For cases where parent component handles milestone updates
      const updatedMilestone = { ...milestone, guaranteed_gains_current: count };
      handleUpdateMilestone(updatedMilestone);
    } else {
      // For cases where we update the gig state directly
      dispatch(
        setGig({
          ...gig,
          milestones: gig.milestones.map(m =>
            m.id === id ? { ...m, guaranteed_gains_current: count } : m
          ),
        })
      );
    }
  };

  const uploadScreenshot =
    !milestone?.initial_screenshot.url &&
    !milestone?.delivery_screenshot.url &&
    (milestone?.has_views ? !milestone?.views_screenshot : true) &&
    milestone?.status !== 'approved';

  const getTitle = () => {
    if (cancelledGig && !milestone.initial_screenshot.url && gigOwner) return '';
    if (cancelledGig) return 'Cancelled Gig';

    if (uploadScreenshot && !gigOwner) return 'Upload Screenshots';
    if (uploadScreenshot && gigOwner) return 'Screenshots';
    return 'Screenshots';
  };

  const readyForDelivery =
    new Date() >= new Date(milestone?.expires_at) && milestone.status !== 'rejected';

  useEffect(() => {
    if (!gig.profile_id) return;

    getRequest({
      endpoint: `profiles/${gig.profile_id}/public_profile`,
      query: { as_advertiser: true },
    })
      .then(profile => {
        if (profile?.inventories) {
          const onlyFansInventory = profile.inventories.find(
            inventory => inventory.influencer_network?.network_id === ONLYFANS_NETWORK_ID
          );

          if (onlyFansInventory?.influencer_network?.username)
            setOnlyFansUsername(onlyFansInventory.influencer_network.username);
        }
      })
      .catch(() => {});
  }, [gig.profile_id]);

  const showDeliverables = gig.profile_id && gig.funded && (gigOwner || influencerAssigned);

  if (!showDeliverables) return null;

  return (
    <div className={styles.deliverables}>
      <div className={styles.heading}>
        <h3 style={cancelledGig ? { color: '#ff4b4b' } : {}}>{getTitle()}</h3>

        {(milestone.active || profile?.allow_early_delivery) &&
          !milestone.initial_screenshot.url &&
          !gigOwner &&
          !milestone?.inventory_space?.event_attendance &&
          !milestone.inventory_space.has_content && (
            <p className="info-box">
              Once you have made the post, take a screenshot of it immediately after and upload as
              proof of post.
            </p>
          )}

        {(milestone.active || profile?.allow_early_delivery) &&
          !gig.c4_campaign_id &&
          milestone.guaranteed_gains &&
          milestone.initial_screenshot.url &&
          !gigOwner && (
            <p className="info-box">
              For guaranteed gains, the buyer will add you to their OnlyFans campaign so you can
              track the analytics. They'll upload a screenshot of the invite in the chat.
            </p>
          )}

        {(milestone.active || profile?.allow_early_delivery) &&
          gig.c4_campaign_id &&
          milestone.guaranteed_gains &&
          milestone.initial_screenshot.url &&
          !gigOwner && (
            <p className="info-box">
              The buyer has connected their OnlyFans account, so the claims count will be updated
              automatically.
            </p>
          )}

        {(milestone.active || profile?.allow_early_delivery) &&
          !gig.c4_campaign_id &&
          milestone.guaranteed_gains &&
          gigOwner &&
          milestone.initial_screenshot.url && (
            <p className="info-box">
              Please invite the{' '}
              <CopyToClipboard text={onlyFansUsername}>
                <span
                  style={{ fontWeight: 700, cursor: 'pointer' }}
                  className="color-main"
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    Toast('success', 'Copied username to clipboard!', {
                      icon: (
                        <IconMainContainer src="/icons/notifications/thumb-up.svg" alt="Thumb Up" />
                      ),
                    });
                  }}
                >
                  {onlyFansUsername}{' '}
                </span>
              </CopyToClipboard>
              to the campaign on OF(Where you created the link) so they can track the sub count and
              upload a screenshot of the invite in the chat.
            </p>
          )}

        {(milestone.active || profile?.allow_early_delivery) &&
          gig.c4_campaign_id &&
          milestone.guaranteed_gains &&
          gigOwner &&
          milestone.initial_screenshot.url && (
            <p className="info-box">
              You're connected to the OnlyFans, so the claims count will be updated automatically.
            </p>
          )}

        {(milestone.active || profile?.allow_early_delivery) &&
          milestone.initial_screenshot.url &&
          !milestone.delivery_screenshot.url &&
          !milestone.inventory_space.has_content &&
          !readyForDelivery &&
          !gigOwner && (
            <p className="info-box">
              Please upload another screenshot of the post to show it has remained up for the full
              time. We will send you a reminder about this{' '}
              {milestone?.inventory_space?.name === 'OnlyFans' ? '2 hours before' : 'when'} it’s
              time to take the post down.
            </p>
          )}

        {readyForDelivery &&
          milestone?.name.includes('DM') &&
          (milestone.active || profile?.allow_early_delivery) &&
          !gigOwner &&
          milestone.initial_screenshot && <p className="info-box">Must crop to specific DM.</p>}

        {(milestone.active || profile?.allow_early_delivery) && (
          <Chip
            label={getLabel()}
            className={clsx('light-purple-chip', {
              ['red-chip']: milestone.rejected,
            })}
          />
        )}
      </div>

      {gig &&
      milestone.status === 'pending' &&
      !milestone.active &&
      !profile?.allow_early_delivery ? (
        <Alert severity="info" style={alertStyleNotActive}>
          This ToDo is not active yet
        </Alert>
      ) : (
        <>
          <MilestoneScreenshots
            milestone={milestone}
            overrideGig={gig}
            handleUpdateMilestone={handleUpdateMilestone}
            agencyView={agencyView}
          />

          <GuaranteedGainsProgress
            c4CampaignId={gig.c4_campaign_id}
            milestone={milestone}
            updateMilestone={updateMilestone}
            influencerAssigned={influencerAssigned}
          />
        </>
      )}
    </div>
  );
};

export default Deliverables;
